/**
 * Phase 8 · C-37 (schema half) — Position TAT service.
 *
 * Reads `ggj_b2b_requisitions_tat_v` (introduced by migration 104) — the
 * view wraps the base table and adds three computed columns:
 *   tat_days_target  = target_close_date - opened_at::date
 *   tat_days_actual  = coalesce(closed_at, now())::date - opened_at::date
 *   tat_breach       = closed_at IS NULL AND now()::date > target_close_date
 *
 * The "owner" of a position is the `approver_user_id` on the requisition
 * (the org_user who owns the hiring decision post-approval). We resolve
 * their display_name via a separate IN-query rather than a PostgREST
 * embed because foreign-key embeds through views aren't reliably exposed.
 * The spec mentions `ggj_b2b_org_members`; this codebase calls the same
 * table `ggj_b2b_org_users` (Keycloak-backed, display_name on the row).
 *
 * Paired UI ships in a separate Sonnet PR (C-37 UI). This service is
 * intentionally minimal — list + setTargetCloseDate only. Richer TAT
 * analytics (by-individual, by-hierarchy) land in C-39 as its own file.
 */
import { supabase } from '../client';

export interface PositionTatRow {
  id: string;
  title: string;
  clientName: string;
  ownerUserId: string | null;
  ownerName: string | null;
  openedAt: string | null;
  targetCloseDate: string | null;
  closedAt: string | null;
  tatDaysTarget: number | null;
  tatDaysActual: number | null;
  tatBreach: boolean;
  status: string;
}

/** Inclusive window against `opened_at`. Both bounds optional. */
export interface PositionTatRange {
  /** ISO 8601 timestamp or date. */
  from?: string;
  /** ISO 8601 timestamp or date. */
  to?: string;
}

// The status set we report on — draft/submitted/approved/on_hold reqs
// don't have a `opened_at` yet (and therefore no TAT to measure).
const REPORTABLE_STATUSES = ['sourcing', 'filled', 'cancelled'] as const;

interface TatViewRow {
  id: string;
  org_id: string;
  client_id: string | null;
  approver_user_id: string | null;
  title: string;
  status: string;
  opened_at: string | null;
  target_close_date: string | null;
  closed_at: string | null;
  tat_days_target: number | null;
  tat_days_actual: number | null;
  tat_breach: boolean | null;
}

interface ClientRow {
  id: string;
  name: string;
}

interface OrgUserRow {
  id: string;
  display_name: string | null;
  email: string | null;
}

function uniqueNonNull(ids: Array<string | null>): string[] {
  const set = new Set<string>();
  for (const id of ids) {
    if (id) set.add(id);
  }
  return Array.from(set);
}

export const positionTatService = {
  /**
   * List TAT rows for an org, one per reportable requisition. Returns an
   * empty array when `orgId` is falsy so callers can render a zero-state
   * without branching.
   */
  async listByOrg(orgId: string, range?: PositionTatRange): Promise<PositionTatRow[]> {
    if (!orgId) return [];

    let query = supabase
      .from('ggj_b2b_requisitions_tat_v')
      .select(
        [
          'id',
          'org_id',
          'client_id',
          'approver_user_id',
          'title',
          'status',
          'opened_at',
          'target_close_date',
          'closed_at',
          'tat_days_target',
          'tat_days_actual',
          'tat_breach',
        ].join(', '),
      )
      .eq('org_id', orgId)
      .in('status', REPORTABLE_STATUSES as unknown as string[])
      .order('target_close_date', { ascending: true, nullsFirst: false });

    if (range?.from) query = query.gte('opened_at', range.from);
    if (range?.to)   query = query.lte('opened_at', range.to);

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as TatViewRow[];
    if (rows.length === 0) return [];

    const clientIds = uniqueNonNull(rows.map((r) => r.client_id));
    const ownerIds  = uniqueNonNull(rows.map((r) => r.approver_user_id));

    // Fan-out lookups. The two queries are independent so fire them in
    // parallel; both are cheap IN-list selects keyed by primary key.
    const [clientsRes, ownersRes] = await Promise.all([
      clientIds.length > 0
        ? supabase
            .from('ggj_b2b_clients')
            .select('id, name')
            .eq('org_id', orgId)
            .in('id', clientIds)
        : Promise.resolve({ data: [] as ClientRow[], error: null }),
      ownerIds.length > 0
        ? supabase
            .from('ggj_b2b_org_users')
            .select('id, display_name, email')
            .eq('org_id', orgId)
            .in('id', ownerIds)
        : Promise.resolve({ data: [] as OrgUserRow[], error: null }),
    ]);

    if (clientsRes.error) throw new Error(clientsRes.error.message);
    if (ownersRes.error)  throw new Error(ownersRes.error.message);

    const clientName = new Map<string, string>();
    for (const c of (clientsRes.data ?? []) as ClientRow[]) {
      clientName.set(c.id, c.name);
    }

    const ownerName = new Map<string, string>();
    for (const u of (ownersRes.data ?? []) as OrgUserRow[]) {
      ownerName.set(u.id, u.display_name ?? u.email ?? 'Unknown');
    }

    return rows.map((r) => ({
      id: r.id,
      title: r.title,
      clientName: r.client_id ? (clientName.get(r.client_id) ?? '') : '',
      ownerUserId: r.approver_user_id,
      ownerName: r.approver_user_id ? (ownerName.get(r.approver_user_id) ?? null) : null,
      openedAt: r.opened_at,
      targetCloseDate: r.target_close_date,
      closedAt: r.closed_at,
      tatDaysTarget: r.tat_days_target,
      tatDaysActual: r.tat_days_actual,
      tatBreach: r.tat_breach ?? false,
      status: r.status,
    }));
  },

  /**
   * Adjust a requisition's target close date. Pass `null` to clear.
   * The BEFORE trigger installed in migration 104 validates that the
   * new date is not earlier than `opened_at` and will raise a DB-level
   * exception if it is — we surface that through the thrown Error so
   * the UI can show the PostgREST message inline on the date picker.
   */
  async setTargetCloseDate(positionId: string, date: string | null): Promise<void> {
    if (!positionId) throw new Error('positionId is required to set target_close_date.');

    const { error } = await supabase
      .from('ggj_b2b_requisitions')
      .update({ target_close_date: date })
      .eq('id', positionId);

    if (error) throw new Error(error.message);
  },
};
